import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import {
  DEFAULT_FLEXXI_REPUTATION_WEIGHTS,
  DEFAULT_FLEXXI_SLOT_TIERS,
  SETTING_DEFS,
  coerceStoredJsonValue,
  type FlexxiEligibleMode,
  type FlexxiReputationWeights,
  type FlexxiSlotTier,
} from "@gaole/shared";
import { api } from "../../lib/api";
import { categoryPathMap, categoryPathOptions } from "../../lib/categoryPaths";
import {
  Button,
  Input,
  NumberField,
  PillSegmentSwitcher,
  SearchSelect,
  SettingsCard,
  Toggle,
} from "../../components/ui";

export const FLEXXI_SETTING_KEYS = SETTING_DEFS.filter((d) => d.group === "flexxi").map((d) => d.key);

interface FlexxiSettingsEditorProps {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: { mutate: (keys: string[]) => void; isPending: boolean };
}

function parseStringArray(value: unknown): string[] {
  const coerced = coerceStoredJsonValue(value);
  if (!Array.isArray(coerced)) return [];
  return coerced.filter((x): x is string => typeof x === "string" && x.length > 0);
}

function parseSlotTiers(value: unknown): FlexxiSlotTier[] {
  const coerced = coerceStoredJsonValue(value);
  if (!Array.isArray(coerced) || !coerced.length) return [...DEFAULT_FLEXXI_SLOT_TIERS];
  return coerced
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const minScore = Number(r.minScore);
      const maxActivePlans = Number(r.maxActivePlans);
      const codes = parseStringArray(r.allowedTemplateCodes);
      if (!Number.isFinite(minScore) || !Number.isFinite(maxActivePlans)) return null;
      return { minScore, maxActivePlans, allowedTemplateCodes: codes };
    })
    .filter((x): x is FlexxiSlotTier => x != null)
    .sort((a, b) => a.minScore - b.minScore);
}

function parseReputationWeights(value: unknown): FlexxiReputationWeights {
  const base = { ...DEFAULT_FLEXXI_REPUTATION_WEIGHTS };
  const coerced = coerceStoredJsonValue(value);
  if (!coerced || typeof coerced !== "object") return base;
  const r = coerced as Record<string, unknown>;
  for (const key of Object.keys(base) as (keyof FlexxiReputationWeights)[]) {
    const n = Number(r[key]);
    if (Number.isFinite(n)) base[key] = n;
  }
  return base;
}

function draftNumber(draft: Record<string, unknown>, key: string, fallback: number): string {
  const v = draft[key];
  if (v === "" || v == null) return String(fallback);
  return String(v);
}

function SlotTiersEditor({
  tiers,
  templateOptions,
  onChange,
}: {
  tiers: FlexxiSlotTier[];
  templateOptions: { value: string; label: string }[];
  onChange: (next: FlexxiSlotTier[]) => void;
}) {
  function updateRow(index: number, patch: Partial<FlexxiSlotTier>) {
    onChange(tiers.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number) {
    onChange(tiers.filter((_, i) => i !== index));
  }

  function addRow() {
    const last = tiers[tiers.length - 1];
    onChange([
      ...tiers,
      {
        minScore: last ? last.minScore + 50 : 0,
        maxActivePlans: last ? last.maxActivePlans + 1 : 1,
        allowedTemplateCodes: templateOptions[0] ? [templateOptions[0].value] : [],
      },
    ]);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        Reputation score tiers control how many active FlexxiPlan contracts a customer may hold and which plan templates
        they can choose.
      </p>
      <div className="overflow-hidden rounded-xl border border-line">
        <div className="grid grid-cols-[88px_88px_minmax(0,1fr)_40px] gap-2 border-b border-line bg-surface-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted">
          <span>Min score</span>
          <span>Max plans</span>
          <span>Templates</span>
          <span />
        </div>
        {tiers.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted">No tiers — add at least one row.</div>
        ) : (
          <div className="divide-y divide-line/60">
            {tiers.map((row, index) => (
              <div
                key={index}
                className="grid grid-cols-[88px_88px_minmax(0,1fr)_40px] items-start gap-2 px-3 py-3"
              >
                <NumberField
                  min={0}
                  step={1}
                  value={row.minScore}
                  onChange={(n) => updateRow(index, { minScore: n })}
                />
                <NumberField
                  min={1}
                  step={1}
                  value={row.maxActivePlans}
                  onChange={(n) => updateRow(index, { maxActivePlans: Math.max(1, n) })}
                />
                <SearchSelect
                  multiple
                  placeholder="Plan templates…"
                  options={templateOptions}
                  value={row.allowedTemplateCodes}
                  onChange={(codes) => updateRow(index, { allowedTemplateCodes: codes })}
                  emptyMessage="Create templates under FlexxiPlan → Templates first."
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mt-1 px-2"
                  onClick={() => removeRow(index)}
                  aria-label="Remove tier"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button type="button" size="sm" variant="outline" onClick={addRow}>
        <Plus size={14} className="mr-1" />
        Add tier
      </Button>
    </div>
  );
}

function ReputationWeightsEditor({
  weights,
  onChange,
}: {
  weights: FlexxiReputationWeights;
  onChange: (next: FlexxiReputationWeights) => void;
}) {
  const fields: { key: keyof FlexxiReputationWeights; label: string; hint?: string }[] = [
    { key: "onTimePayment", label: "On-time payment", hint: "Added when an instalment is paid before grace ends." },
    { key: "latePayment", label: "Late payment", hint: "Applied when a late fee is charged." },
    {
      key: "partialPayment",
      label: "Partial payment",
      hint: "When an instalment payment does not clear the full amount due for that instalment.",
    },
    { key: "completedPlan", label: "Completed plan", hint: "When the customer pays off the plan in full." },
    { key: "defaultedPlan", label: "Defaulted plan", hint: "When a plan is defaulted." },
    { key: "marketplaceRatingBonus", label: "Marketplace rating bonus", hint: "Optional boost from seller ratings." },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map(({ key, label, hint }) => (
        <NumberField
          key={key}
          label={label}
          step={1}
          value={weights[key] ?? 0}
          onChange={(n) => onChange({ ...weights, [key]: n })}
          hint={hint}
        />
      ))}
    </div>
  );
}

function EligibleCategoriesEditor({
  ids,
  onChange,
}: {
  ids: string[];
  onChange: (ids: string[]) => void;
}) {
  const categoriesQuery = useQuery({
    queryKey: ["catalog-categories"],
    queryFn: () =>
      api<{ categories: { id: string; name: string; parentId: string | null }[] }>("/catalog/categories"),
  });
  const categories = useMemo(() => categoriesQuery.data?.categories ?? [], [categoriesQuery.data?.categories]);
  const options = useMemo(
    () => categoryPathOptions(categories).map((c) => ({ value: c.id, label: c.label })),
    [categories],
  );
  const nameById = useMemo(() => categoryPathMap(categories), [categories]);

  return (
    <div className="space-y-3">
      <SearchSelect
        multiple
        label="Eligible categories"
        hint="Only products in these categories can use FlexxiPlan checkout. Leave empty to block all products in this mode."
        placeholder="Select categories…"
        options={options}
        value={ids}
        onChange={onChange}
        emptyMessage={categoriesQuery.isLoading ? "Loading categories…" : "No categories found."}
      />
      {ids.length > 0 ? (
        <ul className="rounded-xl border border-line bg-surface-2/40 px-3 py-2 text-xs text-muted">
          {ids.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2 py-1">
              <span className="text-foreground">{nameById.get(id) ?? id}</span>
              <button
                type="button"
                className="text-muted hover:text-foreground"
                onClick={() => onChange(ids.filter((x) => x !== id))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function EligibleProductsEditor({
  ids,
  onChange,
}: {
  ids: string[];
  onChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!ids.length) return;
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await api<{ product: { id: string; name: string } }>(`/catalog/products/${id}`);
            return [id, res.product.name] as const;
          } catch {
            return [id, id] as const;
          }
        }),
      );
      if (cancelled) return;
      setLabels((prev) => {
        const next = { ...prev };
        for (const [id, name] of entries) {
          if (!next[id]) next[id] = name;
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [ids]);

  const productsQuery = useQuery({
    queryKey: ["flexxi-eligible-products", search],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: "25",
        includeInactive: "true",
        active: "true",
        excludeCreditPacks: "true",
      });
      if (search.trim()) params.set("search", search.trim());
      return api<{ items: { id: string; name: string; sku: string }[] }>(`/catalog/products?${params}`);
    },
  });

  const options = useMemo(() => {
    const items = productsQuery.data?.items ?? [];
    return items
      .filter((p) => !ids.includes(p.id))
      .map((p) => ({
        value: p.id,
        label: p.name,
        hint: p.sku,
      }));
  }, [productsQuery.data?.items, ids]);

  function addProduct(productId: string) {
    if (!productId || ids.includes(productId)) return;
    const item = productsQuery.data?.items.find((p) => p.id === productId);
    if (item) setLabels((prev) => ({ ...prev, [item.id]: item.name }));
    onChange([...ids, productId]);
  }

  return (
    <div className="space-y-3">
      <SearchSelect
        label="Add product"
        placeholder="Search warehouse catalog…"
        options={options}
        value=""
        onChange={addProduct}
        onSearchChange={setSearch}
        serverFiltered
        emptyMessage={productsQuery.isLoading ? "Loading…" : "No matching products."}
        hint="Only listed products in this set are eligible. Empty list blocks all products in this mode."
      />
      <div className="overflow-hidden rounded-xl border border-line">
        <div className="grid grid-cols-[minmax(0,1fr)_40px] gap-2 border-b border-line bg-surface-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted">
          <span>Product</span>
          <span />
        </div>
        {ids.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted">No products selected.</div>
        ) : (
          <ul className="divide-y divide-line/60">
            {ids.map((id) => (
              <li key={id} className="grid grid-cols-[minmax(0,1fr)_40px] items-center gap-2 px-3 py-2">
                <span className="truncate text-sm text-foreground">{labels[id] ?? id}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  onClick={() => onChange(ids.filter((x) => x !== id))}
                  aria-label="Remove product"
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function FlexxiSettingsEditor({ draft, setValue, saveMut }: FlexxiSettingsEditorProps) {
  const eligibleMode = (String(draft["flexxi.eligible_mode"] ?? "ALL") as FlexxiEligibleMode) || "ALL";
  const slotTiers = parseSlotTiers(draft["flexxi.slot_tiers"]);
  const reputation = parseReputationWeights(draft["flexxi.reputation_weights"]);
  const categoryIds = parseStringArray(draft["flexxi.eligible_category_ids"]);
  const productIds = parseStringArray(draft["flexxi.eligible_product_ids"]);

  const templatesQuery = useQuery({
    queryKey: ["flexxi", "templates"],
    queryFn: () => api<{ templates: { code: string; name: string; active: boolean }[] }>("/admin/flexxi/templates"),
  });

  const templateOptions = useMemo(
    () =>
      (templatesQuery.data?.templates ?? [])
        .filter((t) => t.active)
        .map((t) => ({ value: t.code, label: `${t.code} — ${t.name}` })),
    [templatesQuery.data?.templates],
  );

  return (
    <div className="w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">FlexxiPlan settings</h1>
        <p className="mt-0.5 text-xs text-muted">Instalment checkout, eligibility, reputation, and payment reminders.</p>
      </div>

      <SettingsCard
        title="FlexxiPlan"
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(FLEXXI_SETTING_KEYS)}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Toggle
            label="FlexxiPlan enabled"
            checked={Boolean(draft["flexxi.enabled"])}
            onChange={(checked) => setValue("flexxi.enabled", checked)}
          />
          <p className="text-xs text-muted">Requires Vault module. When off, instalment checkout and APIs are hidden.</p>
          <Toggle
            label="Allow on marketplace listings"
            checked={Boolean(draft["flexxi.marketplace_enabled"])}
            onChange={(checked) => setValue("flexxi.marketplace_enabled", checked)}
          />
          <p className="text-xs text-muted">
            Off = FlexxiPlan only on products sold directly by Mr Gaole. Existing marketplace plans keep running.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Minimum principal (MYR)"
              type="number"
              min={0}
              step="0.01"
              value={draftNumber(draft, "flexxi.min_principal", 50)}
              onChange={(e) => setValue("flexxi.min_principal", e.target.value)}
            />
            <Input
              label="Maximum principal (MYR)"
              type="number"
              min={0}
              step="0.01"
              value={draftNumber(draft, "flexxi.max_principal", 50000)}
              onChange={(e) => setValue("flexxi.max_principal", e.target.value)}
            />
          </div>
          <Input
            label="Payment reminder (days before due)"
            type="number"
            min={0}
            step={1}
            value={draftNumber(draft, "flexxi.reminder_days_before", 3)}
            onChange={(e) => setValue("flexxi.reminder_days_before", e.target.value)}
            hint="Customers receive a reminder this many days before an instalment due date."
          />
        </div>
      </SettingsCard>

      <SettingsCard
        title="Shop eligibility"
        description="Which storefront products may offer FlexxiPlan at checkout."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(FLEXXI_SETTING_KEYS)}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-medium text-muted">Eligible products mode</p>
            <PillSegmentSwitcher
              value={eligibleMode}
              onChange={(v) => setValue("flexxi.eligible_mode", v)}
              options={[
                { value: "ALL", label: "All shop items" },
                { value: "CATEGORIES", label: "Categories" },
                { value: "PRODUCTS", label: "Products" },
              ]}
            />
            <p className="mt-2 text-xs text-muted">
              All shop items respects min/max principal only. Categories or Products require at least one selection or
              nothing is eligible.
            </p>
          </div>
          {eligibleMode === "CATEGORIES" ? (
            <EligibleCategoriesEditor
              ids={categoryIds}
              onChange={(next) => setValue("flexxi.eligible_category_ids", next)}
            />
          ) : null}
          {eligibleMode === "PRODUCTS" ? (
            <EligibleProductsEditor
              ids={productIds}
              onChange={(next) => setValue("flexxi.eligible_product_ids", next)}
            />
          ) : null}
        </div>
      </SettingsCard>

      <SettingsCard
        title="Slot tiers"
        footer={
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={saveMut.isPending}
              onClick={() => {
                setValue(
                  "flexxi.slot_tiers",
                  [...slotTiers].sort((a, b) => a.minScore - b.minScore),
                );
                saveMut.mutate(FLEXXI_SETTING_KEYS);
              }}
            >
              Save
            </Button>
          </div>
        }
      >
        <SlotTiersEditor
          tiers={slotTiers}
          templateOptions={templateOptions}
          onChange={(next) => setValue("flexxi.slot_tiers", next)}
        />
      </SettingsCard>

      <SettingsCard
        title="Reputation weights"
        description="Points applied to the customer Flexxi reputation score."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(FLEXXI_SETTING_KEYS)}>
              Save
            </Button>
          </div>
        }
      >
        <ReputationWeightsEditor
          weights={reputation}
          onChange={(next) => setValue("flexxi.reputation_weights", next)}
        />
      </SettingsCard>
    </div>
  );
}
